<template>
  <div class="page-wrapper">
    <div class="content container-fluid">
      <div class="page-header">
        <div class="row">
          <div class="col-sm-12">
            <h3 class="page-title">Tất Cả Thông Báo</h3>
            <ul class="breadcrumb">
              <li class="breadcrumb-item">
                <router-link to="/">Dashboard</router-link>
              </li>
              <li class="breadcrumb-item active">Thông báo</li>
            </ul>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-12">
          <div class="notification-filter-wrapper">
            <div class="notification-filter">
              <button
                @click="setFilterStatus('all')"
                :class="{ active: filterStatus === 'all' }"
                class="filter-btn"
              >
                Tất cả
              </button>
              <button
                @click="setFilterStatus('unread')"
                :class="{ active: filterStatus === 'unread' }"
                class="filter-btn"
              >
                Chưa đọc
                <span v-if="unreadCount > 0" class="unread-badge">{{
                  unreadCount
                }}</span>
              </button>
              <button
                @click="setFilterStatus('read')"
                :class="{ active: filterStatus === 'read' }"
                class="filter-btn"
              >
                Đã đọc
              </button>
            </div>
            <button
              v-if="unreadCount > 0 && notifications.length > 0"
              @click="markAllAsRead"
              class="btn btn-sm btn-outline-primary mark-all-btn"
            >
              <i class="fa fa-check-double me-1"></i> Đánh dấu tất cả đã đọc
            </button>
          </div>

          <div class="card notification-card">
            <div class="card-body">
              <div v-if="isLoading" class="loading-state">
                <div class="spinner-border text-primary" role="status"></div>
                <p>Đang tải thông báo...</p>
              </div>

              <div v-else-if="notifications.length === 0" class="empty-state">
                <i class="fa fa-bell-slash empty-icon"></i>
                <h5 class="empty-title">Không có thông báo nào</h5>
                <p v-if="filterStatus === 'all'" class="empty-text">
                  Bạn chưa có thông báo nào.
                </p>
                <p v-else-if="filterStatus === 'unread'" class="empty-text">
                  Bạn không có thông báo nào chưa đọc.
                </p>
                <p v-else class="empty-text">
                  Bạn không có thông báo nào đã đọc.
                </p>
              </div>

              <ul v-else class="notification-list list-unstyled">
                <li
                  v-for="noti in notifications"
                  :key="noti.id"
                  @click="navigateTo(noti, $event)"
                  :class="['notification-item', { 'is-unread': !noti.daDoc }]"
                >
                  <div class="notification-avatar">
                    <a href="#" @click.stop.prevent class="avatar">
                      <img
                        :alt="noti.tenNguoiGui"
                        :src="noti.avatarNguoiGui"
                        class="rounded-circle"
                        loading="lazy"
                      />
                    </a>
                  </div>

                  <div class="notification-content">
                    <div class="notification-header">
                      <div class="header-left">
                        <span
                          :class="['noti-tag', getTagClass(noti.loaiThongBao)]"
                        >
                          {{ getTagText(noti.loaiThongBao) }}
                        </span>
                      </div>
                      <span class="time">{{
                        formatTimeAgo(noti.thoiGianTao)
                      }}</span>
                    </div>
                    <div class="notification-body" v-html="noti.noiDung"></div>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <nav
            v-if="totalPages > 1 && !isLoading && notifications.length > 0"
            class="pagination-wrapper"
            aria-label="Page navigation"
          >
            <ul class="pagination">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <a
                  class="page-link"
                  href="#"
                  @click.prevent="changePage(currentPage - 1)"
                >
                  <span>&laquo;</span>
                </a>
              </li>
              <li
                v-for="(page, index) in paginatedPages"
                :key="index"
                class="page-item"
                :class="{
                  active: page === currentPage,
                  disabled: typeof page === 'string',
                }"
              >
                <span v-if="typeof page === 'string'" class="page-link">{{
                  page
                }}</span>
                <a
                  v-else
                  class="page-link"
                  href="#"
                  @click.prevent="changePage(page)"
                  >{{ page }}</a
                >
              </li>
              <li
                class="page-item"
                :class="{ disabled: currentPage === totalPages }"
              >
                <a
                  class="page-link"
                  href="#"
                  @click.prevent="changePage(currentPage + 1)"
                >
                  <span>&raquo;</span>
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";
import { useNotificationStore } from "@/stores/notificationStore";

export default {
  name: "DanhSachThongBao",
  computed: {
    ...mapState(useNotificationStore, [
      "isLoading",
      "error",
      "filterStatus",
      "unreadCount",
      "totalPages",
      "currentPage",
      "notifications",
    ]),

    /**
     * Tạo ra một dải phân trang thông minh.
     * Ví dụ: [1, 2, 3, 4, 5, '...', 10] hoặc [1, '...', 4, 5, 6, '...', 10]
     */
    paginatedPages() {
      if (this.totalPages <= 7) {
        return Array.from({ length: this.totalPages }, (_, i) => i + 1);
      }

      const pages = [];
      const currentPage = this.currentPage;
      const totalPages = this.totalPages;

      // Luôn hiển thị trang đầu tiên
      pages.push(1);

      // Xử lý dấu '...' ở đầu
      if (currentPage > 4) {
        pages.push("...");
      }

      // Các trang ở giữa
      const startPage = Math.max(2, currentPage - 2);
      const endPage = Math.min(totalPages - 1, currentPage + 2);

      for (let i = startPage; i <= endPage; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      // Xử lý dấu '...' ở cuối
      if (currentPage < totalPages - 3) {
        pages.push("...");
      }

      // Luôn hiển thị trang cuối cùng
      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }

      return pages;
    },
  },
  methods: {
    ...mapActions(useNotificationStore, [
      "fetchNotifications",
      "handleMarkAsRead",
      "setCurrentPage",
      "setFilterStatus",
      "markAllAsRead",
    ]),

    async navigateTo(notification, evt) {
      if (!notification) return;

      if (!notification.daDoc) {
        try {
          await this.handleMarkAsRead(notification.id);
        } catch (_) {}
      }

      const deep = (notification.deepLink || "").trim();
      const openInNewTab =
        evt && (evt.ctrlKey || evt.metaKey || evt.button === 1);

      if (deep) {
        if (/^https?:\/\//i.test(deep)) {
          if (openInNewTab) window.open(deep, "_blank");
          else window.location.assign(deep);
        } else {
          const path = deep.startsWith("/") ? deep : `/${deep}`;
          if (openInNewTab) {
            const url = this.$router.resolve(path).href;
            window.open(url, "_blank");
          } else {
            this.$router.push(path).catch(() => {});
          }
        }
        return;
      }

      if (notification.loaiDoiTuong && notification.idDoiTuong) {
        const link = `/${String(notification.loaiDoiTuong).toLowerCase()}/${
          notification.idDoiTuong
        }`;
        if (openInNewTab) {
          const url = this.$router.resolve(link).href;
          window.open(url, "_blank");
        } else {
          this.$router.push(link).catch(() => {});
        }
        return;
      }

      if (openInNewTab) {
        const url = this.$router.resolve("/").href;
        window.open(url, "_blank");
      } else {
        this.$router.push("/").catch(() => {});
      }
    },

    changePage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage)
        return;
      this.setCurrentPage(page);
    },

    formatTimeAgo(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const now = new Date();
      const diffSeconds = Math.floor((now - date) / 1000);
      if (diffSeconds < 60) return "Vài giây trước";
      const diffMinutes = Math.floor(diffSeconds / 60);
      if (diffMinutes < 60) return `${diffMinutes} phút trước`;
      const diffHours = Math.floor(diffMinutes / 60);
      if (diffHours < 24) return `${diffHours} giờ trước`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays <= 30) return `${diffDays} ngày trước`;
      // Nếu hơn 30 ngày, hiển thị ngày tháng
      return date.toLocaleDateString("vi-VN");
    },

    getTagClass(loaiThongBao) {
      const classMap = {
        CONG_VIEC: "tag-cong-viec",
        PHAN_CONG: "tag-phan-cong",
        BINH_LUAN: "tag-binh-luan",
        BINH_LUAN_MENTION: "tag-nhac-binh-luan",
        NHAC_NHO: "tag-nhac-nho",
        HANG_NGAY: "tag-hang-ngay",
        THONG_KE: "tag-thong-ke",
        QUA_HAN: "tag-qua-han",
      };
      return classMap[loaiThongBao] || "tag-default";
    },

    getTagText(loaiThongBao) {
      const textMap = {
        CONG_VIEC: "Công việc",
        PHAN_CONG: "Phân công",
        BINH_LUAN: "Bình luận",
        BINH_LUAN_MENTION: "Nhắc tên",
        NHAC_NHO: "Nhắc nhở",
        HANG_NGAY: "Hàng ngày",
        THONG_KE: "Thống kê",
        QUA_HAN: "Quá hạn",
      };
      return textMap[loaiThongBao] || "Chung";
    },
  },
  mounted() {
    this.fetchNotifications(this.currentPage || 1);
  },
  watch: {
    // Theo dõi sự thay đổi của currentPage và gọi lại API
    currentPage(newPage, oldPage) {
      if (newPage !== oldPage) {
        this.fetchNotifications(newPage);
        window.scrollTo(0, 0); // Cuộn lên đầu trang khi chuyển trang
      }
    },
    // Theo dõi sự thay đổi của filter và gọi lại API, reset về trang 1
    filterStatus(newStatus, oldStatus) {
      if (newStatus !== oldStatus) {
        this.setCurrentPage(1, { fetch: false }); // Reset về trang 1 nhưng không fetch
        this.fetchNotifications(1); // Fetch với trang 1 và filter mới
      }
    },
  },
};
</script>

<style scoped>
/* --- Filter Bar --- */
.notification-filter-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}
.notification-filter {
  display: flex;
  gap: 10px;
  background-color: #fff;
  padding: 5px;
  border-radius: 25px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
}
.notification-filter .filter-btn {
  background-color: transparent;
  border: none;
  padding: 8px 18px;
  border-radius: 20px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  color: #4b5563;
  font-size: 0.9rem;
}
.notification-filter .filter-btn.active {
  background-color: #3b82f6;
  color: #fff;
  box-shadow: 0 2px 5px rgba(59, 130, 246, 0.3);
}
.unread-badge {
  background-color: #ef4444;
  color: white;
  padding: 2px 7px;
  font-size: 11px;
  border-radius: 10px;
  margin-left: 6px;
  vertical-align: middle;
}
.mark-all-btn {
  font-size: 0.85rem;
}

/* --- Notification Card & List --- */
.notification-card {
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 15px rgba(0, 0, 0, 0.04);
  border-radius: 12px;
  min-height: 400px;
  position: relative;
  overflow: hidden;
}
.card-body {
  padding: 0;
}
.notification-list {
  margin: 0;
}
.notification-item {
  display: flex;
  align-items: flex-start;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: background-color 0.2s ease, border-left-color 0.2s ease;
  position: relative;
  border-left: 4px solid transparent;
}
.notification-item:last-child {
  border-bottom: none;
}
.notification-item:hover {
  background-color: #f9fafb;
}
.notification-item.is-unread {
  background-color: #eff6ff;
  border-left-color: #3b82f6;
}
.notification-item.is-unread .notification-body {
  font-weight: 500;
  color: #1f2937;
}

/* --- Notification Item Content --- */
.notification-avatar {
  margin-right: 15px;
}
.notification-avatar .avatar img {
  width: 45px;
  height: 45px;
}
.notification-content {
  flex: 1;
}
.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
  gap: 10px;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.notification-header .time {
  font-size: 0.8rem;
  color: #6b7280;
  flex-shrink: 0;
}
.notification-body {
  font-size: 0.95rem;
  color: #4b5563;
  line-height: 1.6;
}
.notification-body >>> p {
  margin-bottom: 0;
}
.notification-body >>> b,
.notification-body >>> strong {
  color: #111827;
}
.notification-body >>> a {
  color: #3b82f6;
  font-weight: 500;
}

/* --- Tags --- */
.noti-tag {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  color: #fff;
  text-transform: capitalize;
}
.tag-cong-viec {
  background-color: #3b82f6;
}
.tag-phan-cong {
  background-color: #14b8a6;
}
.tag-binh-luan {
  background-color: #22c55e;
}
.tag-nhac-nho {
  background-color: #f59e0b;
}
.tag-thong-ke {
  background-color: #8b5cf6;
}
.tag-qua-han {
  background-color: #ef4444;
}
.tag-default {
  background-color: #6b7280;
}
.tag-hang-ngay {
  background-color: #ce0bf5;
}
.tag-nhac-binh-luan {
  background-color: #06c4dd;
}

/* --- States (Loading/Empty) --- */
.empty-state,
.loading-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 3rem 1rem;
  color: #6b7280;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #fff;
}
.empty-icon {
  font-size: 3.5rem;
  color: #d1d5db;
  margin-bottom: 1.5rem;
}
.empty-title {
  color: #1f2937;
  font-weight: 600;
}
.empty-text {
  font-size: 0.95rem;
}
.loading-state p {
  margin-top: 1rem;
  font-weight: 500;
  color: #4b5563;
}

/* --- Pagination --- */
.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
}
.pagination .page-item .page-link {
  border-radius: 8px !important;
  margin: 0 3px;
  border: 1px solid #dee2e6;
  color: #495057;
  min-width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 500;
  transition: all 0.2s ease;
}
.pagination .page-item.active .page-link {
  background-color: #3b82f6;
  border-color: #3b82f6;
  color: #fff;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
}
.pagination .page-item.disabled .page-link {
  color: #adb5bd;
  background-color: #e9ecef;
  border-color: #dee2e6;
}
.pagination .page-item:not(.disabled) .page-link:hover {
  background-color: #f1f5f9;
}
.pagination .page-item span.page-link {
  cursor: default;
  background-color: transparent;
  border: none;
}

/* --- Responsive Styles --- */
@media (max-width: 767px) {
  .notification-filter-wrapper {
    flex-direction: column;
    align-items: stretch;
  }
  .notification-filter {
    justify-content: center;
    width: 100%;
  }
  .mark-all-btn {
    width: 100%;
  }
  .notification-item {
    padding: 0.8rem 1rem;
  }
  .notification-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
  .time {
    align-self: flex-start;
  }

  /* === SỬA LỖI PHÂN TRANG TRÊN DI ĐỘNG === */
  .pagination .page-item .page-link {
    min-width: 36px;
    height: 36px;
    font-size: 0.9rem;
    margin: 0 2px; /* Giảm khoảng cách giữa các nút */
  }
}
</style>
